import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormGroup, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { CategoriesService } from '../services/categories.service';
import {
  Category,
  CreateCategoryDto,
  Mode,
} from '../../../../../utils/interfaces/categories.interface';
import { uniqueValueValidator } from '../validators/unique-value-validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { categoryNameFormatValidator } from '../validators/category-name-format.validator';
import { categoryUrlFormatValidator } from '../validators/category-url-format.validator';
import { IconName } from '@app/utils/enums/icon-enum';
import { PARENT_CATEGORIES } from '@app/utils/constants/categories.constants';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { filter, tap } from 'rxjs';
import { URL_ERROR_MAP, UrlErrorKey } from '../constants';

@Component({
  selector: 'app-add-category',
  standalone: true,
  imports: [
    TranslateModule,
    MatSelectModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    IconComponent,
    ReactiveFormsModule,
    MatOptionModule,
    MatSlideToggle,
  ],
  templateUrl: './add-category.component.html',
  styleUrl: './add-category.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCategoryComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AddCategoryComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly categoriesService = inject(CategoriesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly data = inject<Mode>(MAT_DIALOG_DATA, { optional: true });
  private initialFormValue!: CreateCategoryDto;

  public IconName = IconName;
  public isEditMode = signal(false);
  public urlErrorResponse = signal<string>('');

  public readonly parentCategories = PARENT_CATEGORIES;

  protected addCategoryForm!: FormGroup;
  protected readonly titleKey = computed(() =>
    this.isEditMode()
      ? 'shop-settings.categories.categoriesModal.titleEdit'
      : 'shop-settings.categories.categoriesModal.titleAdd',
  );

  protected readonly submitBtnKey = computed(() =>
    this.isEditMode()
      ? 'shop-settings.categories.categoriesModal.save'
      : 'shop-settings.categories.categoriesModal.add',
  );

  public ngOnInit(): void {
    this.setForm();

    if (this.isEditModeCheck()) {
      this.isEditMode.set(true);
      this.patchForm(this.data?.category as Partial<Category>);

      this.initialFormValue = this.normalizeDto(this.getCreateCategoryDto());

      this.addCategoryForm.get('name')?.updateValueAndValidity();
      this.addCategoryForm.get('url')?.updateValueAndValidity();
    }

    this.addCategoryForm
      .get('url')!
      .valueChanges.pipe(
        tap(() => this.getUrlError()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public onSubmit(): void {
    this.addCategoryForm.markAllAsTouched();

    if (this.addCategoryForm.invalid) {
      return;
    }

    const payload = this.normalizeDto(this.getCreateCategoryDto());

    if (this.isEditMode() && this.isEqual(payload, this.initialFormValue)) {
      return;
    }

    if (this.isEditModeCheck()) {
      this.editCategoryRequest(payload);
    } else {
      this.addCategoryRequest(payload);
    }
  }

  private setForm(): void {
    const currentId = this.data?.category?.id;

    this.addCategoryForm = new FormGroup({
      admin: new FormControl(true),
      name: new FormControl('', [
        Validators.required,
        categoryNameFormatValidator(),
        uniqueValueValidator('name', this.categoriesService, currentId),
      ]),
      url: new FormControl('', [
        Validators.required,
        categoryUrlFormatValidator(),
        uniqueValueValidator('url', this.categoriesService, currentId),
      ]),
      parentCategory: new FormControl(1),
    });
  }

  private isEqual(a: CreateCategoryDto, b: CreateCategoryDto): boolean {
    return (
      a.name === b.name &&
      a.url === b.url &&
      a.visibility === b.visibility &&
      a.parentId === b.parentId
    );
  }

  private normalizeDto(dto: CreateCategoryDto): CreateCategoryDto {
    return {
      ...dto,
      name: dto.name.trim(),
      url: dto.url?.trim(),
    };
  }

  private patchForm(category: Partial<Category>): void {
    this.addCategoryForm.patchValue({
      name: category.name,
      url: category.url,
      admin: category.visibility,
      parentCategory: category.parentId ?? 1,
    });
  }

  private isEditModeCheck(): boolean {
    return this.data?.mode === 'edit' && !!this.data.category;
  }

  public getCreateCategoryDto(): CreateCategoryDto {
    return {
      name: this.addCategoryForm.value.name,
      url: this.addCategoryForm.value.url,
      visibility: this.addCategoryForm.value.admin,
      parentId:
        this.addCategoryForm.value.parentCategory === 1
          ? null
          : this.addCategoryForm.value.parentCategory,
    };
  }

  public onCancelAddCategory(): void {
    if (this.addCategoryForm.pristine) {
      this.dialogRef.close();
      return;
    }

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });

    this.dialogRef.addPanelClass('hidden-dialog');

    confirmRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
      .subscribe(() => {
        this.dialogRef.close();
      });

    confirmRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((confirmed) => !confirmed),
      )
      .subscribe(() => {
        this.dialogRef.removePanelClass('hidden-dialog');
      });
  }

  private addCategoryRequest(payload: CreateCategoryDto) {
    this.categoriesService
      .createNewCategory(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close('success'),
        error: () => {
          this.dialog.open(StatusModalComponent, {
            data: {
              isSuccess: false,
              title: 'shop-settings.categories.errors.addCategoryErrorTitle',
              description: 'shop-settings.categories.errors.addCategoryErrorMessage',
            },
          });
        },
      });
  }

  private editCategoryRequest(payload: CreateCategoryDto) {
    this.categoriesService
      .updateCategory(this.data!.category!.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close('success'),
        error: () => {
          this.dialog.open(StatusModalComponent, {
            data: {
              isSuccess: false,
              title: 'shop-settings.categories.errors.editCategoryErrorTitle',
              description: 'shop-settings.categories.errors.editCategoryErrorMessage',
            },
          });
        },
      });
  }

  public getUrlError(): void {
    const urlControl = this.addCategoryForm.get('url');

    if (!urlControl) {
      return;
    }

    if (!urlControl.errors) {
      this.urlErrorResponse.set('');
      return;
    }

    const firstError = Object.keys(urlControl?.errors || {})[0] as UrlErrorKey;

    if (firstError) {
      const path = URL_ERROR_MAP[firstError] || 'urlStructureInvalid';
      this.urlErrorResponse.set(`shop-settings.categories.errors.${path}`);
    }
  }
}
